import React, {
  useMemo, useEffect, useState, useRef,
} from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { AnimationDirection } from './types';

interface AnimatedEntranceAndExitProps {
  children: React.ReactNode;
  isVisible: boolean;
  duration?: number;
  delay?: number;
  distance?: number;
  direction?: AnimationDirection;
  sx?: SxProps<Theme>;
  component?: React.ElementType;
  unmountOnExit?: boolean;
}

const AnimatedEntranceAndExit: React.FC<AnimatedEntranceAndExitProps> = ({
  children,
  isVisible,
  duration = 0.3,
  delay = 0,
  distance = 20,
  direction = 'up',
  sx = {},
  component = 'div',
  unmountOnExit = false,
}) => {
  // Track if component should be in DOM
  const [shouldRender, setShouldRender] = useState(isVisible || !unmountOnExit);
  // Track current animation state: 'entering', 'entered', 'exiting', 'exited'
  const [animationState, setAnimationState] = useState<'entering' | 'entered' | 'exiting' | 'exited'>(
    isVisible ? 'entering' : 'exited',
  );
  const prevVisibleRef = useRef(isVisible);

  const entranceAnimationName = useMemo(
    () => `fadeInEntrance-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  const exitAnimationName = useMemo(
    () => `fadeOutExit-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  useEffect(() => {
    // Only react to actual changes in isVisible
    if (prevVisibleRef.current === isVisible) {
      return;
    }
    prevVisibleRef.current = isVisible;

    if (isVisible) {
      // Start entrance animation
      setShouldRender(true);
      // Use setTimeout to ensure DOM is updated before animation starts
      setTimeout(() => {
        setAnimationState('entering');
      }, 10);

      // After animation completes, set to 'entered'
      const timer = setTimeout(() => {
        setAnimationState('entered');
      }, (duration + delay) * 1000 + 20);

      return () => { clearTimeout(timer); };
    }
    // Start exit animation
    setAnimationState('exiting');

    // After animation completes
    const timer = setTimeout(() => {
      setAnimationState('exited');
      if (unmountOnExit) {
        setShouldRender(false);
      }
    }, (duration + delay) * 1000);

    return () => { clearTimeout(timer); };
  }, [isVisible, duration, delay, unmountOnExit]);

  const getEntranceTransform = (dir: AnimationDirection) => {
    switch (dir) {
      case 'up':
        return `translateY(${distance}px)`;
      case 'down':
        return `translateY(-${distance}px)`;
      case 'left':
        return `translateX(${distance}px)`;
      case 'right':
        return `translateX(-${distance}px)`;
      default:
        return `translateY(${distance}px)`;
    }
  };

  const getExitTransform = (dir: AnimationDirection) => {
    // Exit is the opposite direction of entrance
    // If direction='up' (enters from below), exit downward (same transform)
    // If direction='right' (enters from left), exit to left (same transform)
    switch (dir) {
      case 'up':
        return `translateY(${distance}px)`; // Exit downward (opposite of entering upward)
      case 'down':
        return `translateY(-${distance}px)`; // Exit upward (opposite of entering downward)
      case 'left':
        return `translateX(${distance}px)`; // Exit to the right (opposite of entering leftward)
      case 'right':
        return `translateX(-${distance}px)`; // Exit to the left (opposite of entering rightward)
      default:
        return `translateY(${distance}px)`;
    }
  };

  if (!shouldRender) {
    return null;
  }

  const getStyles = () => {
    const baseStyles = {
      ...sx,
    };

    switch (animationState) {
      case 'entering':
        return {
          ...baseStyles,
          animation: `${entranceAnimationName} ${duration}s ease-in-out`,
          animationDelay: `${delay}s`,
          animationFillMode: 'forwards',
          opacity: 0,
          visibility: 'hidden' as const,
          transform: getEntranceTransform(direction),
          [`@keyframes ${entranceAnimationName}`]: {
            from: {
              visibility: 'hidden',
              opacity: 0,
              transform: getEntranceTransform(direction),
            },
            to: {
              opacity: 1,
              transform: 'translate(0)',
              visibility: 'visible',
            },
          },
        };

      case 'entered':
        return {
          ...baseStyles,
          opacity: 1,
          visibility: 'visible' as const,
          transform: 'translate(0)',
        };

      case 'exiting':
        return {
          ...baseStyles,
          animation: `${exitAnimationName} ${duration}s ease-in-out`,
          animationDelay: `${delay}s`,
          animationFillMode: 'forwards',
          opacity: 1,
          visibility: 'visible' as const,
          transform: 'translate(0)',
          [`@keyframes ${exitAnimationName}`]: {
            from: {
              opacity: 1,
              transform: 'translate(0)',
              visibility: 'visible',
            },
            to: {
              visibility: 'hidden',
              opacity: 0,
              transform: getExitTransform(direction),
            },
          },
        };

      case 'exited':
        return {
          ...baseStyles,
          opacity: 0,
          visibility: 'hidden' as const,
          transform: getExitTransform(direction),
        };

      default:
        return baseStyles;
    }
  };

  return (
    <Box
      component={component}
      sx={getStyles()}
    >
      {children}
    </Box>
  );
};

export default AnimatedEntranceAndExit;
